import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { ChevronRight, ChevronDown, BookOpen, FileText, Search, Loader2 } from 'lucide-react';
import { getLearningCatalogue } from '../../services/api';
import PDFViewer from '../../components/PDFViewer';

const LearningPage = () => {
  const [catalogue, setCatalogue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedModules, setExpandedModules] = useState({});

  useEffect(() => {
    const fetchCatalogue = async () => {
      try {
        const response = await getLearningCatalogue();
        const flatItems = response.data.categories || []; // Backend returns flat list in 'categories' field

        const structuredData = transformToHierarchy(flatItems);
        setCatalogue({ categories: structuredData });

        // Auto-expand first category
        if (structuredData.length > 0) {
          setExpandedCategories({ [structuredData[0].id]: true });
        }
      } catch (error) {
        console.error('Failed to fetch learning catalogue:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCatalogue();
  }, []);

  const transformToHierarchy = (flatItems) => {
    const categoriesMap = {};

    flatItems.forEach((item) => {
      const catName = item.category || 'General Learning';
      const modName = item.module || 'Introductory Materials';

      if (!categoriesMap[catName]) {
        categoriesMap[catName] = {
          id: catName.toLowerCase().replace(/\s+/g, '-'),
          name: catName,
          modules: {},
        };
      }

      if (!categoriesMap[catName].modules[modName]) {
        categoriesMap[catName].modules[modName] = {
          id: `${catName}-${modName}`.toLowerCase().replace(/\s+/g, '-'),
          name: modName,
          units: [],
        };
      }

      categoriesMap[catName].modules[modName].units.push(item);
    });

    return Object.values(categoriesMap).map((cat) => ({
      ...cat,
      modules: Object.values(cat.modules),
    }));
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const toggleModule = (moduleId) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
  };

  const filterUnits = (categories) => {
    if (!searchQuery.trim()) return categories;

    return categories.map((cat) => ({
      ...cat,
      modules: cat.modules.map((mod) => ({
        ...mod,
        units: mod.units.filter((unit) =>
          unit.title.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter((mod) => mod.units.length > 0),
    })).filter((cat) => cat.modules.length > 0);
  };

  if (loading) {
    return (
      <div className="p-6 md:p-12 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const filteredCategories = catalogue ? filterUnits(catalogue.categories) : [];

  return (
    <div className="p-6 md:p-12" data-testid="learning-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-2">
          Learning
        </h1>
        <p className="text-gray-600">
          Access educational materials, ritual guides, and learning resources.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Sidebar - Module List */}
        <div className="lg:order-1">
          <Card className="border-gray-100">
            <CardContent className="p-4">
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search learning materials..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="learning-search-input"
                />
              </div>

              {/* Category Tree */}
              <ScrollArea className="h-[500px]" data-testid="learning-sidebar">
                <div className="space-y-2">
                  {filteredCategories.map((category) => (
                    <Collapsible
                      key={category.id}
                      open={expandedCategories[category.id]}
                      onOpenChange={() => toggleCategory(category.id)}
                    >
                      <CollapsibleTrigger className="flex items-center gap-2 w-full p-2 text-left hover:bg-gray-100 rounded-md transition-colors">
                        {expandedCategories[category.id] ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                        <BookOpen className="h-4 w-4 text-emerald-600" />
                        <span className="font-medium text-gray-900 text-sm">{category.name}</span>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pl-6">
                        {category.modules.map((module) => (
                          <Collapsible
                            key={module.id}
                            open={expandedModules[module.id]}
                            onOpenChange={() => toggleModule(module.id)}
                          >
                            <CollapsibleTrigger className="flex items-center gap-2 w-full p-2 text-left hover:bg-gray-100 rounded-md transition-colors">
                              {expandedModules[module.id] ? (
                                <ChevronDown className="h-3 w-3 text-gray-400" />
                              ) : (
                                <ChevronRight className="h-3 w-3 text-gray-400" />
                              )}
                              <span className="text-gray-700 text-sm">{module.name}</span>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="pl-5">
                              {module.units.map((unit) => (
                                <button
                                  key={unit.id}
                                  onClick={() => setSelectedUnit(unit)}
                                  className={`flex items-center gap-2 w-full p-2 text-left rounded-md transition-colors text-sm ${selectedUnit?.id === unit.id
                                      ? 'bg-emerald-100 text-emerald-900'
                                      : 'hover:bg-gray-100 text-gray-600'
                                    }`}
                                  data-testid={`learning-unit-${unit.id}`}
                                >
                                  <FileText className="h-3 w-3" />
                                  <span className="truncate">{unit.title}</span>
                                </button>
                              ))}
                            </CollapsibleContent>
                          </Collapsible>
                        ))}
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:order-2" data-testid="learning-content">
          {selectedUnit ? (
            <Card className="border-gray-100">
              <CardContent className="p-6">
                <h2 className="font-heading text-2xl font-semibold text-gray-900 mb-4">
                  {selectedUnit.title}
                </h2>

                {selectedUnit.type === 'pdf' ? (
                  <PDFViewer url={selectedUnit.url} title={selectedUnit.title} />
                ) : (
                  <div className="prose prose-gray max-w-none" data-testid="learning-text-content">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {selectedUnit.content}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-gray-100 bg-gradient-to-br from-gray-50 to-white">
              <CardContent className="p-12 text-center">
                <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                  Select a Learning Unit
                </h3>
                <p className="text-gray-500">
                  Choose a topic from the sidebar to view its content.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default LearningPage;
